import { useAuth } from "./useAuth";

/**
 * IAM API 연동 훅
 * - 모든 엔드포인트는 요청/응답 규격을 그대로 반영
 * - status: true/false 기반 성공여부
 * - permission_denied, already_workspace 플래그 처리
 */
export default function useIAM() {
  const { api } = useAuth();

  /** IAM 목록: 멤버 리스트 + 마스터 여부 */
  const fetchIAMList = async () => {
    try {
      const { data } = await api.get("/iam/list");
      // data: { status, data: IamList[], master: boolean }
      if (!data?.status) {
        return { ok: false, members: [], isMaster: false };
      }
      const raw = Array.isArray(data.data) ? data.data : [];
      const members = raw.map((m) => ({
        id: m.id,
        user_id: m.user_id,
        user_name: m.user_name,
        grade: m.grade, // "master" | "member"
        email: m.email,
        hp: m.hp,
        created_at: m.created_at, // ISO
        is_me: !!m.is_me,
      }));
      return { ok: true, members, isMaster: !!data.master };
    } catch (e) {
      return { ok: false, members: [], isMaster: false };
    }
  };

  /** 내 정보 수정 (본인만) */
  const updateSelf = async ({ user_name, email, hp }) => {
    try {
      const { data } = await api.post("/iam/update", {
        user_name,
        email,
        hp,
      });
      return !!data?.status;
    } catch (e) {
      return false;
    }
  };

  /** 멤버 초대 (마스터만) */
  const inviteMember = async (email) => {
    try {
      const { data } = await api.post("/iam/member/invite", { email });
      return {
        ok: !!data?.status,
        permissionDenied: !!data?.permission_denied,
      };
    } catch (e) {
      return { ok: false, permissionDenied: false };
    }
  };

  /** 멤버 추방 (마스터만) */
  const banMember = async (id) => {
    try {
      const { data } = await api.post("/iam/member/ban", { id });
      return {
        ok: !!data?.status,
        permissionDenied: !!data?.permission_denied,
      };
    } catch (e) {
      return { ok: false, permissionDenied: false };
    }
  };

  /** 초대 승인 대기 명단 조회 (마스터만) */
  const fetchApprovalRoster = async () => {
    try {
      const { data } = await api.get("/iam/approval/roster");
      if (data?.permission_denied) {
        return { ok: true, permissionDenied: true, items: [] };
      }
      if (!data?.status) {
        return { ok: false, permissionDenied: false, items: [] };
      }
      const rows = Array.isArray(data.data) ? data.data : [];
      const items = rows.map((r) => ({
        id: r.id,
        enterprise_id: r.enterprise_id,
        user_id: r.user_id,
        approval_stage: r.approval_stage,
        email: r.email,
        hp: r.hp,
        created_at: r.created_at,
      }));
      return { ok: true, permissionDenied: false, items };
    } catch (e) {
      return { ok: false, permissionDenied: false, items: [] };
    }
  };

  /** 초대 승인 OK (마스터만) */
  const approveRoster = async (id) => {
    try {
      const { data } = await api.post("/iam/approval/roster/ok", { id });
      return {
        ok: !!data?.status,
        permissionDenied: !!data?.permission_denied,
        alreadyWorkspace: !!data?.already_workspace,
      };
    } catch (e) {
      return { ok: false, permissionDenied: false, alreadyWorkspace: false };
    }
  };

  /** 초대 승인 거절 (마스터만) */
  const refuseRoster = async (id) => {
    try {
      const { data } = await api.post("/iam/approval/roster/refuse", {
        id,
      });
      return {
        ok: !!data?.status,
        permissionDenied: !!data?.permission_denied,
      };
    } catch (e) {
      return { ok: false, permissionDenied: false };
    }
  };

  /** 초대 메일 리스트 조회 */
  const fetchInviteEmailList = async () => {
    try {
      const { data } = await api.get("/iam/invite/email_list_20250822");
      if (!data?.status) {
        return { ok: false, items: [] };
      }
      const raw = Array.isArray(data.data) ? data.data : [];
      const items = raw.map((item) => ({
        id: item.id,
        enterprise_id: item.enterprise_id,
        invite_email: item.invite_email,
        created_at: item.created_at,
      }));
      return { ok: true, items };
    } catch (e) {
      return { ok: false, items: [] };
    }
  };

  /** 초대 메일 취소 (마스터만) */
  const cancelInviteEmail = async (id) => {
    try {
      const { data } = await api.post("/iam/invite/email_cancel_20250822", {
        id,
      });
      return {
        ok: !!data?.status,
        permissionDenied: !!data?.permission_denied,
      };
    } catch (e) {
      return { ok: false, permissionDenied: false };
    }
  };

  /** 회사 정보 조회 */
  const fetchEnterpriseInfo = async () => {
    try {
      const { data } = await api.get("/iam/enterprise_20250822");
      if (!data?.status) {
        return { ok: false, enterprise: null };
      }
      const ent = data.data || {};
      return {
        ok: true,
        enterprise: {
          id: ent.id,
          owner_id: ent.owner_id,
          display_name: ent.display_name || "",
          usage_plan: ent.usage_plan || "",
          description: ent.description || "",
          created_at: ent.created_at,
        },
      };
    } catch (e) {
      return { ok: false, enterprise: null };
    }
  };

  /** 회사 정보 수정 (마스터만) */
  const updateEnterpriseInfo = async ({ display_name, description }) => {
    try {
      const { data } = await api.post("/iam/enterprise/update_20250822", {
        display_name,
        description,
      });
      return {
        ok: !!data?.status,
        permissionDenied: !!data?.permission_denied,
      };
    } catch (e) {
      return { ok: false, permissionDenied: false };
    }
  };

  /**
   * 전체 IAM 대시보드 데이터 한번에 가져오기
   * - 멤버 목록, 승인 대기, 초대 메일, 회사 정보
   */
  const fetchAllIAMData = async () => {
    try {
      const [membersRes, approvalsRes, invitesRes, enterpriseRes] =
        await Promise.all([
          fetchIAMList(),
          fetchApprovalRoster(),
          fetchInviteEmailList(),
          fetchEnterpriseInfo(),
        ]);

      return {
        ok: true,
        members: membersRes.members || [],
        isMaster: membersRes.isMaster || false,
        approvals: approvalsRes.items || [],
        invites: invitesRes.items || [],
        enterprise: enterpriseRes.enterprise,
        approvalsPermissionDenied: approvalsRes.permissionDenied || false,
      };
    } catch (e) {
      return {
        ok: false,
        members: [],
        isMaster: false,
        approvals: [],
        invites: [],
        enterprise: null,
        approvalsPermissionDenied: false,
      };
    }
  };

  return {
    fetchIAMList,
    updateSelf,
    inviteMember,
    banMember,
    fetchApprovalRoster,
    approveRoster,
    refuseRoster,
    fetchInviteEmailList,
    cancelInviteEmail,
    fetchEnterpriseInfo,
    updateEnterpriseInfo,
    fetchAllIAMData,
  };
}
